/**
 * SkillUploadDialog — presigned upload flow with two-format support and
 * upload disambiguation (O-770).
 *
 *   1. POST /v1/skills:upload-url    → presigned PUT URL (carries `format`)
 *   2. PUT  <presignedUrl>           → raw blob (with progress)
 *   3. POST /v1/skills:finalize-upload → server validates + either creates a
 *      SkillVersion OR returns `disambiguationRequired` when the upload's name
 *      collides with existing catalog rows out of lineage / ambiguously.
 *
 * On disambiguation the dialog stays open and renders a chooser: bump one of
 * the matching skills, or create a new skill. The re-finalize call reuses the
 * same `object_key` with an explicit `action`.
 *
 * Accepts `.skill` / `.zip` archives (≤ SKILL_MAX_SIZE_BYTES) or a bare
 * `SKILL.md` (≤ SKILL_MD_MAX_SIZE_BYTES).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    createSkillUploadUrl,
    finalizeSkillUpload,
    uploadSkillBlob,
    detectUploadFormat,
    UPLOAD_FORMAT,
    SKILL_MAX_SIZE_BYTES,
    SKILL_MD_MAX_SIZE_BYTES,
} from "../../lib/skillsClient.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatRelativeTime } from "./skillBadges.jsx";

const inputCls =
    "min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";
const primaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer";
const secondaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer";

const NEW_SKILL = "__new__";

function formatBytes(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// One candidate + the "new skill" sentinel, rendered as a radio list.
function DisambiguationPanel({ matches, skillName, chosen, onChoose, onCancel, onSubmit, inFlight }) {
    const prompt =
        matches.length === 1
            ? `There is 1 existing skill named \`${skillName}\`. Is this upload a new version of it, or a new skill?`
            : `There are ${matches.length} existing skills named \`${skillName}\`. Is this a new version of one of them, or a new skill?`;
    return (
        <div className="grid gap-[10px]">
            <p className={`${NOTE_CLASS} text-[12px] leading-[1.4]`}>{prompt}</p>
            <div className="grid gap-[6px]">
                {matches.map((m) => {
                    const skillId = m.skillId ?? m.skill_id;
                    const label = m.displayDisambiguator ?? m.display_disambiguator ?? skillId;
                    const desc = m.latestDescription ?? m.latest_description ?? "";
                    const updated = m.lastUpdatedAt ?? m.last_updated_at;
                    const inLineage = m.isInLineage ?? m.is_in_lineage;
                    return (
                        <label
                            key={skillId}
                            className={`grid gap-[3px] rounded-[2px] border px-[10px] py-[8px] cursor-pointer ${
                                chosen === skillId ? "border-[color:var(--green)] bg-[color:var(--green-soft)]" : "border-[color:var(--line)]"
                            }`}
                        >
                            <span className="flex items-center gap-[8px]">
                                <input
                                    type="radio"
                                    name="disambig"
                                    value={skillId}
                                    checked={chosen === skillId}
                                    onChange={() => onChoose(skillId)}
                                    disabled={inFlight}
                                />
                                <span className="text-[12px] text-[color:var(--ink)] font-medium">{label}</span>
                                {inLineage ? (
                                    <span className="inline-flex items-center rounded-[2px] border border-[color:var(--green)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium text-[color:var(--ink)]">
                                        known version
                                    </span>
                                ) : null}
                                {updated ? (
                                    <span className={`${NOTE_CLASS} text-[10px]`}>updated {formatRelativeTime(updated)}</span>
                                ) : null}
                            </span>
                            {desc ? (
                                <span className={`${NOTE_CLASS} text-[10px] pl-[22px]`} title={desc}>
                                    {desc.length > 80 ? `${desc.slice(0, 80)}…` : desc}
                                </span>
                            ) : null}
                        </label>
                    );
                })}
                <label
                    className={`grid gap-[3px] rounded-[2px] border px-[10px] py-[8px] cursor-pointer ${
                        chosen === NEW_SKILL ? "border-[color:var(--green)] bg-[color:var(--green-soft)]" : "border-[color:var(--line)]"
                    }`}
                >
                    <span className="flex items-center gap-[8px]">
                        <input
                            type="radio"
                            name="disambig"
                            value={NEW_SKILL}
                            checked={chosen === NEW_SKILL}
                            onChange={() => onChoose(NEW_SKILL)}
                            disabled={inFlight}
                        />
                        <span className="text-[12px] text-[color:var(--ink)] font-medium">This is a new skill</span>
                    </span>
                    <span className={`${NOTE_CLASS} text-[10px] pl-[22px]`}>
                        Create a new skill row with this content as version 1.
                    </span>
                </label>
            </div>
            <div className="flex items-center justify-end gap-[6px]">
                <button type="button" onClick={onCancel} disabled={inFlight} className={secondaryBtnCls}>
                    Cancel
                </button>
                <button type="button" onClick={onSubmit} disabled={!chosen || inFlight} className={primaryBtnCls}>
                    {inFlight ? "Saving…" : "Continue"}
                </button>
            </div>
        </div>
    );
}

export default function SkillUploadDialog({ onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [tags, setTags] = useState("");
    // idle | minting | uploading | finalizing | disambiguating | refinalizing
    const [phase, setPhase] = useState("idle");
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    // Set when the server asks us to disambiguate; carries the data the
    // re-finalize call needs plus the chooser's current selection.
    const [disambig, setDisambig] = useState(null); // { objectKey, format, tagArr, matches, skillName }
    const [chosen, setChosen] = useState(null);

    useEffect(() => { setError(null); }, [file, tags]);

    const format = useMemo(() => (file ? detectUploadFormat(file.name) : null), [file]);
    const sizeCap = format === UPLOAD_FORMAT.SKILL_MD ? SKILL_MD_MAX_SIZE_BYTES : SKILL_MAX_SIZE_BYTES;
    const unsupported = file && !format;
    const tooLarge = file && format && file.size > sizeCap;
    const busy = phase !== "idle" && phase !== "disambiguating";
    const canSubmit = file && format && !tooLarge && !busy && phase === "idle";

    const onPickFile = (e) => {
        const f = e.target.files?.[0] ?? null;
        setFile(f);
        setProgress(0);
        setDisambig(null);
        setChosen(null);
    };

    const runFinalize = useCallback(
        async ({ objectKey, format: fmt, tagArr, action }) => {
            const res = await finalizeSkillUpload({ objectKey, tags: tagArr, format: fmt, action });
            const req = res?.disambiguationRequired ?? res?.disambiguation_required;
            const version = res?.version;
            if (req) {
                setDisambig({
                    objectKey,
                    format: fmt,
                    tagArr,
                    matches: Array.isArray(req.matches) ? req.matches : [],
                    skillName: req.name ?? "",
                });
                setChosen(null);
                setPhase("disambiguating");
                return;
            }
            // Success (version populated, or a legacy flat response).
            setPhase("idle");
            onUploaded?.(res);
            onClose?.();
        },
        [onUploaded, onClose],
    );

    const submit = useCallback(async () => {
        if (!file || !format || tooLarge) return;
        setError(null);
        setPhase("minting");
        try {
            const urlResp = await createSkillUploadUrl({ sizeBytes: file.size, format });
            const uploadUrl = urlResp?.uploadUrl ?? urlResp?.upload_url;
            const objectKey = urlResp?.objectKey ?? urlResp?.object_key;
            if (!uploadUrl || !objectKey) {
                throw new Error("Upload URL response missing uploadUrl / objectKey");
            }
            setPhase("uploading");
            await uploadSkillBlob(uploadUrl, file, (loaded, total) => {
                setProgress(total > 0 ? loaded / total : 0);
            });
            setPhase("finalizing");
            const tagArr = tags.split(",").map((t) => t.trim()).filter(Boolean);
            await runFinalize({ objectKey, format, tagArr, action: { kind: "auto" } });
        } catch (err) {
            console.error("[skills] upload", err);
            setError(err.message || String(err));
            setPhase("idle");
        }
    }, [file, format, tooLarge, tags, runFinalize]);

    const submitDisambiguation = useCallback(async () => {
        if (!disambig || !chosen) return;
        const action =
            chosen === NEW_SKILL
                ? { kind: "create_new_skill" }
                : { kind: "bump_version_of_skill_id", skillId: chosen };
        setError(null);
        setPhase("refinalizing");
        try {
            await runFinalize({
                objectKey: disambig.objectKey,
                format: disambig.format,
                tagArr: disambig.tagArr,
                action,
            });
        } catch (err) {
            console.error("[skills] finalize (disambiguated)", err);
            setError(err.message || String(err));
            setPhase("disambiguating");
        }
    }, [disambig, chosen, runFinalize]);

    // Disambiguation view replaces the picker once the server asks for it.
    if (disambig) {
        return (
            <div className="grid gap-[14px]">
                <DisambiguationPanel
                    matches={disambig.matches}
                    skillName={disambig.skillName}
                    chosen={chosen}
                    onChoose={setChosen}
                    onCancel={onClose}
                    onSubmit={submitDisambiguation}
                    inFlight={phase === "refinalizing"}
                />
                {error ? <span className="text-[11px] text-red-400">{error}</span> : null}
            </div>
        );
    }

    return (
        <div className="grid gap-[14px]">
            <div className="grid gap-[4px]">
                <span className={NOTE_CLASS}>Archive or SKILL.md</span>
                <input
                    type="file"
                    accept=".skill,.zip,application/zip,.md,text/markdown"
                    onChange={onPickFile}
                    disabled={busy}
                    className="text-[12px] text-[color:var(--ink)] file:mr-[8px] file:rounded-[2px] file:border file:border-[color:var(--line)] file:bg-transparent file:px-[10px] file:py-[5px] file:text-[11px] file:uppercase file:tracking-[0.04em] file:text-[color:var(--muted)] hover:file:text-[color:var(--ink)] file:cursor-pointer"
                />
                <span className={`${NOTE_CLASS} text-[10px]`}>
                    {file ? (
                        <>
                            <span className={(tooLarge || unsupported) ? "text-red-400" : "text-[color:var(--ink)]"}>
                                {file.name}
                            </span>{" "}
                            ({formatBytes(file.size)})
                            {tooLarge ? `; exceeds ${formatBytes(sizeCap)} limit` : ""}
                            {format === UPLOAD_FORMAT.SKILL_MD ? "; SKILL.md" : format === UPLOAD_FORMAT.SKILL_ARCHIVE ? "; archive" : ""}
                        </>
                    ) : (
                        `Pick a .skill/.zip archive (max ${formatBytes(SKILL_MAX_SIZE_BYTES)}) or a SKILL.md (max ${formatBytes(SKILL_MD_MAX_SIZE_BYTES)}).`
                    )}
                </span>
                {unsupported ? (
                    <div className="rounded-[2px] border border-amber-400/40 bg-amber-400/5 px-[10px] py-[6px] text-[10px] text-amber-300 leading-[1.4]">
                        Unsupported file type. Upload a <code className="font-mono text-amber-200">.skill</code>/<code className="font-mono text-amber-200">.zip</code> archive or a <code className="font-mono text-amber-200">.md</code> file.
                    </div>
                ) : null}
            </div>

            <label className="grid gap-[4px]">
                <span className={NOTE_CLASS}>Tags <span className="text-[10px]">(optional, comma-separated)</span></span>
                <input
                    type="text"
                    className={inputCls}
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="security, code-review"
                    disabled={busy}
                />
            </label>

            {phase === "uploading" ? (
                <div className="grid gap-[4px]">
                    <span className={`${NOTE_CLASS} text-[10px]`}>Uploading… {Math.round(progress * 100)}%</span>
                    <div className="h-[4px] w-full bg-[color:var(--line)] overflow-hidden rounded-[2px]">
                        <div className="h-full bg-[color:var(--green)] transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
                    </div>
                </div>
            ) : phase === "minting" ? (
                <span className={`${NOTE_CLASS} text-[11px]`}>Requesting upload URL…</span>
            ) : phase === "finalizing" ? (
                <span className={`${NOTE_CLASS} text-[11px]`}>Finalizing…</span>
            ) : null}

            {error ? <span className="text-[11px] text-red-400">{error}</span> : null}

            <div className="flex items-center justify-end gap-[6px]">
                <button type="button" onClick={onClose} disabled={busy} className={secondaryBtnCls}>
                    Cancel
                </button>
                <button type="button" onClick={submit} disabled={!canSubmit} className={primaryBtnCls}>
                    Upload
                </button>
            </div>
        </div>
    );
}
